import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule, NzCheckboxOption } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ServicenumberListComponent } from 'src/app/routes/servicenumber/list/servicenumber-list.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'aiff-setting-detail',
  templateUrl: './aiff-setting-detail.component.html',
  styleUrls: ['./aiff-setting-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzDividerModule,
    NzGridModule,
    NzSwitchModule,
    NzCheckboxModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class AiffSettingDetailComponent implements OnInit {
  // 表单
  settingForm!: FormGroup;
  // 加载状态
  loading = false;
  // 提交状态
  submitting = false;
  // 是否为新增
  isAdd = true;
  // AIFF ID
  aiffId: string = '';
  tenantId: string = '';
  // 设置 ID
  settingId: string | null = null;

  isEdit = false;
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  displayTypeOptions: Array<{ label: string; value: string }> = [];

  // 修改 roomTypeOptions 的格式
  roomTypeOptions: NzCheckboxOption[] = [];

  displayLocationOptions: Array<{ label: string; value: string }> = [];

  deviceOptions: Array<{ label: string; value: string }> = [];
  popupSizeOptions: Array<{ label: string; value: string }> = [];

  serviceNumberOptions: Array<{ label: string; value: string }> = [];
  uploadHeaders = {}; // 设置上传请求头

  constructor(
    private readonly fb: FormBuilder,
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modal: NzModalService
  ) {
    this.settingForm = this.fb.group({
      serviceNumberIds: [[]],
      displayType: [null, [Validators.required]],
      roomType: [[]],
      displayLocation: [null, [Validators.required]],
      supportDevice: [null, [Validators.required]],
      popupSize: [null, [Validators.required]]
    });

    // 初始化选项数组
    this.displayTypeOptions = [
      { label: this.i18n.fanyi('tenant.aiff.setting.display.type.frame'), value: 'Frame' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.type.applist'), value: 'AppList' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.type.room'), value: 'Room' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.type.contact-index'), value: 'ContactIndex' }
    ];

    this.displayLocationOptions = [
      { label: this.i18n.fanyi('tenant.aiff.setting.display.location.toolbar'), value: 'Toolbar' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.location.extend'), value: 'Extend' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.location.embed'), value: 'Embed' },
      { label: this.i18n.fanyi('tenant.aiff.setting.display.location.message-menu'), value: 'MessageMenu' }
    ];

    this.deviceOptions = [
      { label: this.i18n.fanyi('tenant.aiff.setting.support.device.mobile'), value: 'Mobile' },
      { label: this.i18n.fanyi('tenant.aiff.setting.support.device.desktop'), value: 'Desktop' }
    ];

    this.popupSizeOptions = [
      { label: this.i18n.fanyi('tenant.aiff.setting.popup.size.full'), value: 'Full' },
      { label: this.i18n.fanyi('tenant.aiff.setting.popup.size.half'), value: 'Half' }
    ];

    this.roomTypeOptions = [
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.one'), value: 'One' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.person'), value: 'Person' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.friend'), value: 'Friend' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.system'), value: 'System' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.discuss'), value: 'Discuss' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.group'), value: 'Group' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.group-owner'), value: 'GroupOwner' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.business-customer'), value: 'BusinessCustomer' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.business-employee'), value: 'BusinessEmployee' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.business-secretary'), value: 'BusinessSecretary' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.service-number-customer'), value: 'ServiceNumberCustomer' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.service-number-employee'), value: 'ServiceNumberEmployee' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.service-number-agent'), value: 'ServiceNumberAgent' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.service-number-group'), value: 'ServiceNumberGroup' },
      { label: this.i18n.fanyi('tenant.aiff.setting.room.type.object'), value: 'Object' }
    ];
  }

  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  ngOnInit(): void {
    // 从模态窗口数据中获取参数
    if (this.nzModalData) {
      this.aiffId = this.nzModalData.aiffId ?? '';
      this.settingId = this.nzModalData.settingId || null;
      this.isAdd = this.nzModalData.isAdd !== undefined ? this.nzModalData.isAdd : true;
      this.tenantId = this.nzModalData.tenantId ?? '';
    }

    if (!this.isAdd && this.settingId) {
      this.loadSettingData(this.settingId);
    }

    // 监听displayType变化，动态调整验证规则
    this.settingForm.get('displayType')?.valueChanges.subscribe(value => {
      const roomTypeControl = this.settingForm.get('roomType');
      const displayLocationControl = this.settingForm.get('displayLocation');

      if (value === 'Room') {
        // 如果选择了聊天室类型，添加必填验证
        roomTypeControl?.setValidators([Validators.required]);
        displayLocationControl?.setValidators([Validators.required]);
      } else {
        // 否则移除验证
        roomTypeControl?.clearValidators();
        displayLocationControl?.clearValidators();
      }

      // 更新验证状态
      roomTypeControl?.updateValueAndValidity();
      displayLocationControl?.updateValueAndValidity();
    });
  }

  // 加载设置数据
  loadSettingData(id: string): void {
    this.loading = true;
    this.httpService
      .post(`${ConstantUtil.AIFF_SETTING_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.settingForm.patchValue(res.data || {});
          // 禁用键字段编辑
          this.settingForm.get('key')?.disable();
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('tenant.aiff.setting.detail.load.failed'));
        }
      });
  }

  // 保存设置
  saveSetting(): void {
    // 验证表单
    if (this.settingForm.get('displayType')?.value === 'Room') {
      // 如果是聊天室类型，确保roomType和displayLocation已填写
      const roomTypeControl = this.settingForm.get('roomType');
      const displayLocationControl = this.settingForm.get('displayLocation');

      let hasErrors = false;

      if (!roomTypeControl?.value || roomTypeControl.value.length === 0) {
        roomTypeControl?.setErrors({ required: true });
        hasErrors = true;
      } else {
        roomTypeControl?.setErrors(null);
      }

      if (!displayLocationControl?.value) {
        displayLocationControl?.setErrors({ required: true });
        hasErrors = true;
      } else {
        displayLocationControl?.setErrors(null);
      }

      // 如果有错误，标记控件为dirty并更新验证状态
      if (hasErrors) {
        roomTypeControl?.markAsDirty();
        displayLocationControl?.markAsDirty();
        roomTypeControl?.updateValueAndValidity();
        displayLocationControl?.updateValueAndValidity();
        return;
      }
    }

    if (!this.settingForm.valid) {
      Object.values(this.settingForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }
    this.isEdit = true;
    this.submitting = true;
    const formData = this.settingForm.getRawValue(); // 获取包括禁用字段的值
    const requestParams = {
      ...formData,
      aiffId: this.aiffId,
      ...(!this.isAdd && (!Array.isArray(formData.serviceNumberIds) || formData.serviceNumberIds.length === 0)
        ? { clearServiceNumberIds: true }
        : {})
    };

    if (!this.isAdd && this.settingId) {
      requestParams.id = this.settingId;
    }

    const apiUrl = this.isAdd ? ConstantUtil.AIFF_SETTING_CREATE : ConstantUtil.AIFF_SETTING_UPDATE;

    this.httpService
      .postFormData(apiUrl, requestParams, null)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(
            this.isAdd ? this.i18n.fanyi('tenant.aiff.setting.create.success') : this.i18n.fanyi('tenant.aiff.setting.update.success')
          );
          this.cancel();
        } else {
          this.message.error(
            res.msg ||
              (this.isAdd ? this.i18n.fanyi('tenant.aiff.setting.create.failed') : this.i18n.fanyi('tenant.aiff.setting.update.failed'))
          );
        }
      });
  }

  // 取消
  cancel(): void {
    if (this.modalRef) {
      this.modalRef.close(this.isEdit);
    }
  }

  openServiceNumberSelector(): void {
    const selectedIds = this.settingForm.get('serviceNumberIds')?.value || [];

    const selectModal = this.modal.create({
      nzTitle: this.i18n.fanyi('tenant.aiff.setting.select.service.number'),
      nzContent: ServicenumberListComponent,
      nzWidth: '800px',
      nzBodyStyle: { height: '600px', overflow: 'auto' },
      nzData: {
        isSelector: true,
        selectedIds: [...selectedIds],
        tenantId: this.tenantId
      },
      nzFooter: [
        {
          label: this.i18n.fanyi('common.cancel'),
          onClick: () => selectModal.destroy()
        },
        {
          label: this.i18n.fanyi('common.confirm'),
          type: 'primary',
          onClick: componentInstance => {
            const selectedServiceNumbers = componentInstance?.getSelectedServiceNumbers() || [];
            this.settingForm.get('serviceNumberIds')?.setValue(selectedServiceNumbers.map(item => item.id));
            this.loadServiceNumberOptions(selectedServiceNumbers);
            selectModal.destroy();
            return false;
          }
        }
      ]
    });
  }
  loadServiceNumberOptions(selectedServiceNumbers?: any[]): void {
    if (selectedServiceNumbers && selectedServiceNumbers.length > 0) {
      // 如果有传入已选择的服务号，直接使用
      this.serviceNumberOptions = selectedServiceNumbers.map(item => ({
        label: item.name,
        value: item.id
      }));
    }
  }
}
